import { useState, useEffect } from "react";
import { useToast } from "../../contexts/ToastContext";

import {
    Drawer,
    DrawerContent,
    DrawerNavigation,
} from "@progress/kendo-react-layout";
import { FaPlusCircle, FaTimesCircle } from "react-icons/fa";
import "../Styles.css";
import { FormDropDownList, FormInput, FormNumericTextBox, FormTextArea } from "../../components/FormComps/FormComponents";
import { CreateQuoteTemplate, QuoteTemplate, QuoteTemplateFile, addNewQuoteTemplate, getQuoteTemplateById, getQuoteTemplateFiles, getQuoteTemplates, updateQuoteTemplate } from "../../data/quoteData";

const QuoteTemplates = () => {
    /** Is the sidebar expanded? */
    const [expanded, setExpanded] = useState(false);
    /** Table row style taken from table render */
    const trStyle = { backgroundColor: "rgb(245, 249, 251)", border: "1px solid rgb(216, 229, 237)" };
    /** Toast Hook */
    const { showToast } = useToast();
    /** All templates */
    const [quoteTemplates, setQuoteTemplates] = useState<QuoteTemplate[]>([]);
    /** Selected Quote */
    const [selectedQuote, setSelectedQuote] = useState<QuoteTemplate>({} as QuoteTemplate);
    /** Toggle adding a new quote */
    const [addingQuote, setAddingQuote] = useState(false);
    /** sidebar title */
    const [sidebarTitle, setSidebarTitle] = useState("Quote Templates");
    /** new quote template */
    const newQuoteTemplate = {
        name: "",
        shortDescription: "",
        worksDescription: "",
        qualificationsDescription: "",
        totalCost: 0,
        templateFile: {
            fileId: 0,
            fileName: "",
            fileUrl: "",
        }
    }
    /** new quote object */
    const [newQuote, setNewQuote] = useState<CreateQuoteTemplate>(newQuoteTemplate);
    /** list of files */
    const [quoteFiles, setQuoteFiles] = useState<QuoteTemplateFile[]>([]);


    /**
     * Retrieves the text message templates and sets them in the component state.
     * @returns {Promise<void>} A promise that resolves when the templates are retrieved and set.
     */
    const getQuotes = async (): Promise<void> => {
        const quotes = await getQuoteTemplates();
        setQuoteTemplates(quotes);
    };

    /**
     * Handles the click event when a quote template is selected.
     * @param template - The selected quote template.
     */
    const onTemplateClick = async (template: QuoteTemplate) => {
        const item = await getQuoteTemplateById(template.quoteTemplateId);
        setSelectedQuote(item);
        setExpanded(true);
        setAddingQuote(false);
        setSidebarTitle(`${template.name} Template`)
    };

    /**
     * Handles the change event for input fields.
     * Updates the selected quote state with the new value.
     * 
     * @param {Object} event - The event object containing the target element.
     */
    const handleChange = (event: any, type: "create" | "update") => {
        const { name, value } = event.target;
        if (type === "create") {
            setNewQuote((prev) => ({
                ...prev,
                [name]: value,
            }));
        } else {
            setSelectedQuote((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    }

    /**
     * Handles the form submission for updating a quote template.
     * 
     * @returns A promise that resolves to void.
     */
    const onSubmit = async (): Promise<void> => {
        updateQuoteTemplate(selectedQuote.quoteTemplateId, selectedQuote)
            .then(() => {
                showToast("Quote template updated successfully", "success");
                getQuotes();
            })
            .catch(() => {
                showToast("There was an error updating the quote template", "error");
            });
    };

    /**
     * Handles the click event when the "Add" button is clicked.
     * Sets the state variables to control the component's behavior.
     */
    const onAddClick = () => {
        setExpanded(true);
        setAddingQuote(true);
        setSidebarTitle("Add New Quote Template");
    }

    /**
     * Adds a new quote template.
     * 
     * @async
     * @returns {Promise<void>} A promise that resolves when the quote template is added successfully.
     */
    const addQuote = async () => {
        addNewQuoteTemplate(newQuote)
        .then(() => {
            showToast("Quote template added successfully", "success");
            setNewQuote(newQuoteTemplate);
            getQuotes();
        })
        .catch(() => {
            showToast("There was an error adding the quote template", "error");
        })
    };

    /**
     * Retrieves the quote template files and sets them in the state.
     * @returns {Promise<void>} A promise that resolves when the quote template files are retrieved and set in the state.
     */
    const getQuoteFiles = async () => {
        const files = await getQuoteTemplateFiles();
        setQuoteFiles(files);
    }

    useEffect(() => {
        getQuotes();
        getQuoteFiles();
    }, []);

    return (
        <div>
            <Drawer
                expanded={expanded}
                position={"end"}
                width={750}
                mode="overlay"
                animation={{
                    duration: 400,
                }}
                onOverlayClick={() => setExpanded(false)}
                onSelect={() => setExpanded(false)}
            >
                <DrawerContent>
                    <DrawerNavigation>
                        <div className="form__title__container">
                            <h1 className="form__title">{sidebarTitle}</h1>
                            <FaTimesCircle
                                className="closeform"
                                onClick={() => setExpanded(false)}
                            />
                        </div>
                        <div style={{ padding: "20px" }}>
                            {
                                selectedQuote && (
                                    <>
                                    <FormDropDownList 
                                        data={quoteFiles.map(file => ({ text: file.fileName, value: file.fileId }))}
                                        defaultText={selectedQuote?.templateFile?.fileName}
                                        defaultValue={selectedQuote?.templateFile?.fileId}
                                        onChange={(e: any) => {
                                            if(newQuote) { 
                                                setNewQuote((prev) => ({
                                                    ...prev,
                                                    templateFile: {
                                                        fileId: e.value,
                                                        fileName: e.text,
                                                        fileUrl: quoteFiles.find(file => file.fileId === e.value)?.fileUrl || "",
                                                    }
                                                }))
                                            } else { 
                                                setSelectedQuote((prev) => ({
                                                    ...prev,
                                                    templateFile: {
                                                        fileId: e.value,
                                                        fileName: e.text,
                                                        fileUrl: quoteFiles.find(file => file.fileId === e.value)?.fileUrl || "",
                                                        quoteTemplateFileId: quoteFiles.find(file => file.fileId === e.value)?.quoteTemplateFileId || 0
                                                    }
                                                }))
                                            }
                                        
                                        }}
                                        label={"Select Template File"}
                                    />
                                    <FormInput
                                        defaultValue={addingQuote ? newQuote.name : selectedQuote.name}
                                        name={"name"}
                                        label={"Template Name"}
                                        onChange={(e: any) => handleChange(e, newQuote ? "create" : "update")} />
        
                                    <FormTextArea
                                        defaultValue={addingQuote ? newQuote.shortDescription : selectedQuote.shortDescription}
                                        name={"shortDescription"}
                                        label={"Short Description"}
                                        onChange={(e: any) => handleChange(e, newQuote ? "create" : "update")} />
        
                                    <FormTextArea
                                        defaultValue={addingQuote ? newQuote.worksDescription : selectedQuote.worksDescription}
                                        name={"worksDescription"}
                                        label={"Works Description"}
                                        onChange={(e: any) => handleChange(e, newQuote ? "create" : "update")} />
        
                                    <FormTextArea
                                        defaultValue={addingQuote ? newQuote.qualificationsDescription : selectedQuote.qualificationsDescription}
                                        name={"qualificationsDescription"}
                                        label={"Qualifcations Description"}
                                        onChange={(e: any) => handleChange(e, newQuote ? "create" : "update")} />
        
                                    <FormNumericTextBox
                                        value={addingQuote ? newQuote.totalCost : selectedQuote.totalCost}
                                        name={"totalCost"}
                                        label={"Total Cost"}
                                        onChange={(e: any) => handleChange(e, newQuote ? "create" : "update")} />                                    
                                    </>
                                )
                            }
                        </div>


                        <div style={{ padding: "20px" }}>
                            <button
                                onClick={() => {
                                   addingQuote ? addQuote() : onSubmit();
                                }}
                                className='form__edit__button accept'
                            >
                               {addingQuote ? "Add Quote" : "Update Quote"}
                            </button>
                        </div>
                    </DrawerNavigation>
                    <div className="grid__title__container">
                        <div className="grid__header__background">
                            <div className="grid__title">
                                <div>Quote Templates</div>
                                <div
                                    onClick={() => onAddClick()}
                                    className="grid__addrecord__button"
                                >
                                    <FaPlusCircle style={{ fontSize: "14pt", marginRight: "1rem" }} />{" "}
                                    <p style={{ margin: 0 }}>Add new quote</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <table className="grid__table">
                        <thead>
                            <tr>
                                <th style={{ color: "black", fontWeight: "bold" }}>Templates</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                quoteTemplates.map(item => (
                                    <tr style={trStyle} key={item.quoteTemplateId} onClick={() => onTemplateClick(item)}>
                                        <td>{item.name}</td>
                                    </tr>
                                ))
                            }
                        </tbody>
                    </table>


                </DrawerContent>
            </Drawer>
        </div>
    );
};

export default QuoteTemplates;
